/**
 * Component extraction and processing utilities for Thesys C1 components.
 */

import { decodeHtmlEntities } from './htmlHelpers';
import { THESYS_COMPONENT_REGEX } from '../constants/chatConstants';

/**
 * Extracts Thesys C1 components from content string.
 * Components are embedded in <content thesys="true"> tags with JSON inside.
 * 
 * @param content - Content string potentially containing C1 components
 * @returns Array of component JSON strings
 */
export function extractC1Components(content: string): string[] {
  const components: string[] = [];
  
  // Look for <content thesys="true"> tags with JSON inside
  // The regex matches: <content thesys="true">{...JSON...}</content>
  // It uses non-greedy matching to handle multiple components
  // Reset regex lastIndex to ensure fresh search
  const regex = new RegExp(THESYS_COMPONENT_REGEX.source, THESYS_COMPONENT_REGEX.flags);
  let match;
  
  while ((match = regex.exec(content)) !== null) {
    let jsonContent = match[1].trim();
    
    // Decode HTML entities (Thesys C1 returns &quot; instead of ")
    jsonContent = decodeHtmlEntities(jsonContent);
    
    try {
      // Validate it's valid JSON
      JSON.parse(jsonContent);
      components.push(jsonContent);
    } catch (e) {
      // If it's not valid JSON, try to fix common issues
      console.warn("C1 component contains invalid JSON, attempting to fix:", jsonContent.substring(0, 100));
      
      // Try one more time after additional cleanup
      try {
        // Remove any remaining HTML entities or fix common issues
        const cleaned = jsonContent
          .replace(/&quot;/g, '"')
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>');
        JSON.parse(cleaned);
        components.push(cleaned);
      } catch (e2) {
        console.error("C1 component JSON is still invalid after cleanup:", e2);
      }
    }
  }
  
  return components;
}

/**
 * Removes C1 component tags from text content.
 * Removes both JSON and simple text C1 components, plus any context tags.
 * 
 * @param content - Content string potentially containing C1 tags
 * @returns Cleaned content without C1 tags
 */
export function removeC1Tags(content: string): string {
  if (!content) return "";

  let cleaned = content;

  // Remove <content thesys="true">...</content> tags (both JSON and text)
  // Use non-greedy matching to handle multiple tags
  cleaned = cleaned.replace(/<content\s+thesys\s*=\s*["']true["']>[\s\S]*?<\/content>/gi, "");

  // Also remove any <context>...</context> tags that might follow
  cleaned = cleaned.replace(/<context>[\s\S]*?<\/context>/gi, "");

  // Remove any remaining thesys-related tags
  cleaned = cleaned.replace(/<content\s+thesys[^>]*>[\s\S]*?<\/content>/gi, "");

  return cleaned.trim();
}

